import { api } from '../../fetchWrapper';
import { logger } from '../../../utils/logger';
import { API_ENDPOINTS, getEndpoint } from '../../apiConstants';
import type { FailureMailConfigData, FailureMailConfigApiResponse } from './failureMailConfig.types';

// Standardized GET function for failure mail configs list
export async function fetchFailureMailConfigs({
  clientCode,
  firstResult = 0,
  maxResult = 10,
  clientId,
  configName,
  status,
  searched = true,
}: {
  clientCode: string;
  firstResult?: number;
  maxResult?: number;
  clientId?: number;
  configName?: string;
  status?: string;
  searched?: boolean;
}): Promise<FailureMailConfigData[]> {
  const params: Record<string, any> = {
    firstResult,
    maxResult,
    searched,
  };

  if (clientId) params.clientId = clientId;
  if (configName) params.configName = configName;
  if (status) params.status = status;

  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.FAILURE_MAIL_CONFIG.LIST);
  const response: any[] = await api.get<any[]>(endpoint, params);
  
  // Parse the array response format: [id, clientId, notInErrorPatternId, jobId, errorName, jobName, clientName]
  return response.map((configArray: any) => {
    if (Array.isArray(configArray) && configArray.length >= 7) {
      return {
        id: configArray[0], // failure mail id
        clientId: configArray[1], // clientid
        notInErrorPatternId: configArray[2], // notInErrorPatternId
        jobId: configArray[3], // jobid
        errorName: configArray[4] || '', // Error Name
        jobName: configArray[5] || '', // Job Name
        clientName: configArray[6] || '', // Client Name
      };
    } else {
      // Fallback for unexpected format
      return {
        id: 0,
        clientId: 0,
        notInErrorPatternId: 0,
        jobId: 0,
        errorName: '',
        jobName: '',
        clientName: '',
      };
    }
  });
}

// GET failure mail configs count
export async function fetchFailureMailConfigsCount({
  clientCode,
  clientId,
  configName,
  status,
  searched = true,
}: {
  clientCode: string;
  clientId?: number;
  configName?: string;
  status?: string;
  searched?: boolean;
}): Promise<number> {
  const params: Record<string, any> = {
    searched,
  };

  if (clientId) params.clientId = clientId;
  if (configName) params.configName = configName;
  if (status) params.status = status;

  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.FAILURE_MAIL_CONFIG.COUNT);
  
  try {
    const response = await api.get(endpoint, params);
    
    // Handle different response formats
    if (typeof response === 'number') {
      return response;
    } else if (typeof response === 'string') {
      // Handle response like "1 8" - extract the count
      const parts = response.trim().split(' ');
      if (parts.length > 0) {
        const countValue = parseInt(parts[parts.length - 1], 10); // Take the last number
        return isNaN(countValue) ? 0 : countValue;
      }
      return 0;
    } else if (response && typeof response === 'object' && 'count' in response) {
      const countValue = (response as any).count;
      return typeof countValue === 'number' ? countValue : 0;
    } else {
      logger.warn('Unexpected count response format:', response);
      return 0;
    }
  } catch (error) {
    logger.error('Error fetching failure mail configs count:', error);
    return 0;
  }
}

// GET failure mail config by ID
export async function fetchFailureMailConfigById({
  clientCode,
  configId,
}: {
  clientCode: string;
  configId: number;
}): Promise<FailureMailConfigData> {
  const endpoint = getEndpoint(clientCode, `${API_ENDPOINTS.FAILURE_MAIL_CONFIG.LIST}${configId}`);
  const response: any = await api.get<any>(endpoint);
  
  // Parse the array response format
  if (Array.isArray(response) && response.length >= 7) {
    return {
      id: response[0], // failure mail id
      clientId: response[1], // clientid
      notInErrorPatternId: response[2], // notInErrorPatternId
      jobId: response[3], // jobid
      errorName: response[4] || '', // Error Name
      jobName: response[5] || '', // Job Name
      clientName: response[6] || '', // Client Name
    };
  } else {
    // Fallback for unexpected format
    return {
      id: 0,
      clientId: 0,
      notInErrorPatternId: 0,
      jobId: 0,
      errorName: '',
      jobName: '',
      clientName: '',
    };
  }
}

// CREATE failure mail config
export async function createFailureMailConfig({
  clientCode,
  payload,
}: {
  clientCode: string;
  payload: any;
}): Promise<FailureMailConfigData> {
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.FAILURE_MAIL_CONFIG.CREATE);
  const response: any = await api.post<any>(endpoint, payload);
  
  // Parse the array response format
  if (Array.isArray(response) && response.length >= 7) {
    return {
      id: response[0], // failure mail id
      clientId: response[1], // clientid
      notInErrorPatternId: response[2], // notInErrorPatternId
      jobId: response[3], // jobid
      errorName: response[4] || '', // Error Name
      jobName: response[5] || '', // Job Name
      clientName: response[6] || '', // Client Name
    };
  } else {
    // Fallback for unexpected format
    return {
      id: 0,
      clientId: 0,
      notInErrorPatternId: 0,
      jobId: 0,
      errorName: '',
      jobName: '',
      clientName: '',
    };
  }
}

// UPDATE failure mail config - PUT to failureMailConfigRest/ (base path, id in payload), returns { success, object, messages }
export async function updateFailureMailConfig({
  clientCode,
  payload,
}: {
  clientCode: string;
  payload: {
    createdBy?: number | null;
    creationDate?: number | null;
    lastUpdateDate?: number;
    lastUpdatedBy?: number | null;
    clientId: number;
    id: number;
    jobDetailId: number;
    notInErrorPatternId: number;
  };
}): Promise<{ success: boolean; object?: any; messages?: string[] }> {
  const endpoint = getEndpoint(clientCode, API_ENDPOINTS.FAILURE_MAIL_CONFIG.UPDATE);
  return api.put<any>(endpoint, payload);
}

// DELETE failure mail config
export async function deleteFailureMailConfig({
  clientCode,
  configId,
}: {
  clientCode: string;
  configId: number;
}): Promise<void> {
  const endpoint = getEndpoint(clientCode, `${API_ENDPOINTS.FAILURE_MAIL_CONFIG.DELETE}${configId}`);
  return api.delete(endpoint);
}

// TEST failure mail config
export async function testFailureMailConfig({
  clientCode,
  configId,
}: {
  clientCode: string;
  configId: number;
}): Promise<any> {
  const endpoint = getEndpoint(clientCode, `${API_ENDPOINTS.FAILURE_MAIL_CONFIG.LIST}${configId}/test`);
  return api.post(endpoint);
}
